import { useMemo, useRef, useState, useEffect } from 'react';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { DraftV1 } from '@frontend/api-sms-draft';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { useAlert } from '@frontend/design-system';

type UseSmartDraftProps = {
  smartDraftMessages: SMS[];
  threadId?: string;
  aiBotStatusActive?: boolean;
};

export function useSmartDraft({ smartDraftMessages, threadId, aiBotStatusActive }: UseSmartDraftProps) {
  const [userDismissedCard, setUserDismissedCard] = useState(false);
  const lastSmartDraftMessages = useRef<string | null>(null);
  const stableMessagesRef = useRef<{ ids: string; messages: SMS[] }>({ ids: '', messages: [] });
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const alert = useAlert();
  const { t } = useTranslation();

  const hasSmartDraftsEnabled = getFeatureFlagValue('inbox-smart-drafts');
  const resolvedThreadId = threadId || smartDraftMessages[0]?.threadId;

  const resetHook = () => {
    setUserDismissedCard(false);
    lastSmartDraftMessages.current = null;
    stableMessagesRef.current = { ids: '', messages: [] };
  };

  useEffect(() => {
    if (resolvedThreadId) {
      resetHook();
    }
  }, [resolvedThreadId]);

  const { stableMessages, serializedMessageData, hasInbound } = useMemo(() => {
    if (!smartDraftMessages?.length) {
      return { stableMessages: [], serializedMessageData: '', hasInbound: false };
    }

    const currentIds = smartDraftMessages
      .map((msg) => `${msg.id}-${msg.locationId}`)
      .sort()
      .join('|');
    const { ids: previousIds, messages: previousMessages } = stableMessagesRef.current;

    let finalMessages: SMS[];
    if (currentIds !== previousIds) {
      // IDs changed - update stable reference and clean up old one
      stableMessagesRef.current = { ids: currentIds, messages: smartDraftMessages };
      finalMessages = smartDraftMessages;
    } else {
      // IDs same - reuse stable reference
      finalMessages = previousMessages.length ? previousMessages : smartDraftMessages;
    }

    return {
      stableMessages: finalMessages,
      serializedMessageData: currentIds,
      hasInbound: finalMessages.some((sms) => sms.direction === Direction.DIRECTION_INBOUND),
    };
  }, [smartDraftMessages]);

  // Check if we should generate a smart draft
  const shouldGenerateSmartDraft = useMemo(() => {
    if (!hasSmartDraftsEnabled || !serializedMessageData || !!aiBotStatusActive) return false;

    if (!hasInbound) return false;
    return serializedMessageData !== lastSmartDraftMessages.current;
  }, [hasSmartDraftsEnabled, serializedMessageData, hasInbound, aiBotStatusActive]);

  const closeSmartDraft = () => {
    setUserDismissedCard(true);
  };

  const noSmartDraftMessageHandler = () => {
    closeSmartDraft();
    alert.error(t('Error generating smart draft'));
  };

  const smartDraftGenerateSuccessHandler = (message?: string) => {
    if (!message) {
      noSmartDraftMessageHandler();
      return;
    }
    lastSmartDraftMessages.current = serializedMessageData;
  };

  const { data, isFetching: isLoadingSmartDraft } = DraftV1.Queries.useGenerateSmartDraftQuery({
    request: { messages: stableMessages },
    options: {
      enabled: shouldGenerateSmartDraft,
      onSuccess: ({ suggestedMessage }) => {
        setUserDismissedCard(false);
        smartDraftGenerateSuccessHandler(suggestedMessage);
      },
      onError: (error: any) => {
        noSmartDraftMessageHandler();
        sentry.error({
          error,
          topic: 'messages',
          team: 'fe-messaging',
          severityLevel: 'error',
        });
      },
    },
  });

  // Check if we should show smart draft card
  const showSmartDraftCard = useMemo(() => {
    if (
      !hasSmartDraftsEnabled ||
      !serializedMessageData ||
      userDismissedCard ||
      !data?.suggestedMessage ||
      !!aiBotStatusActive
    )
      return false;

    return hasInbound;
  }, [
    hasSmartDraftsEnabled,
    serializedMessageData,
    userDismissedCard,
    data?.suggestedMessage,
    hasInbound,
    aiBotStatusActive,
  ]);

  return {
    smartDraft: data?.suggestedMessage ?? '',
    showSmartDraftCard,
    isLoadingSmartDraft,
    closeSmartDraft,
  };
}
